import React, { useState, useEffect } from 'react';
import { Inmueble } from '../../types/Inmueble';
import { operacionTicketService } from '../../services/operacionTicketService';
import DateInput from '../common/DateInput';
import './GenerarTicketsModal.css';

interface GenerarTicketsModalProps {
  isOpen: boolean;
  inmueble: Inmueble | null;
  onSuccess: () => void;
  onClose: () => void;
}

const today = () => new Date().toISOString().split('T')[0];

const GenerarTicketsModal: React.FC<GenerarTicketsModalProps> = ({ isOpen, inmueble, onSuccess, onClose }) => {
  const [numOperacion, setNumOperacion] = useState<string>('');
  const [valorInicial, setValorInicial] = useState<string>('10000');
  const [fechaEmision, setFechaEmision] = useState<string>(today());
  const [valorContablePre, setValorContablePre] = useState<string>('');
  const [valorContablePost, setValorContablePost] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNumOperacion('');
      setValorInicial('10000');
      setFechaEmision(today());
      setValorContablePre('');
      setValorContablePost('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen || !inmueble) return null;

  const costeInversion = Number(inmueble.costeInversion) || 0;
  const vi = Number(valorInicial) || 0;
  const vcp = Number(valorContablePost) || 0;

  const ticketsAGenerar = vi > 0 ? Math.floor(costeInversion / vi) : 0;
  const porcentaje = vi > 0 && vcp > 0 ? ((vi / vcp) * 100).toFixed(4) : '-';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inmueble.id) return;
    setLoading(true);
    setError('');
    try {
      await operacionTicketService.crearOperacion(inmueble.id, {
        inmuebleId: inmueble.id,
        numOperacion: Number(numOperacion),
        valorInicial: vi,
        fechaEmision,
        valorContablePre: valorContablePre ? Number(valorContablePre) : undefined,
        valorContablePost: vcp,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al generar los tickets');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="generar-tickets-overlay" onClick={onClose}>
      <div className="generar-tickets-modal" onClick={(e) => e.stopPropagation()}>
        <div className="generar-tickets-header">
          <h3>Generar Tickets</h3>
          <button className="generar-tickets-close" onClick={onClose} type="button">×</button>
        </div>

        {error && <div className="generar-tickets-error">{error}</div>}

        <form onSubmit={handleSubmit} className="generar-tickets-form">
          <div className="generar-tickets-field">
            <label>Inmueble</label>
            <input type="text" value={inmueble.direccion || inmueble.idProyecto || ''} disabled />
          </div>

          <div className="generar-tickets-row">
            <div className="generar-tickets-field">
              <label>Nº Operación *</label>
              <input
                type="number"
                value={numOperacion}
                onChange={(e) => setNumOperacion(e.target.value)}
                min="1"
                required
              />
            </div>
            <div className="generar-tickets-field">
              <label>Valor inicial (€)</label>
              <input
                type="number"
                value={valorInicial}
                onChange={(e) => setValorInicial(e.target.value)}
                min="0.01"
                step="0.01"
                required
              />
            </div>
          </div>

          <div className="generar-tickets-field">
            <label>Fecha emisión</label>
            <DateInput
              value={fechaEmision}
              onChange={(e) => setFechaEmision(e.target.value)}
              required
            />
          </div>

          <div className="generar-tickets-row">
            <div className="generar-tickets-field">
              <label>Valor contable antes (€)</label>
              <input
                type="number"
                value={valorContablePre}
                onChange={(e) => setValorContablePre(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
            <div className="generar-tickets-field">
              <label>Valor contable después (€) *</label>
              <input
                type="number"
                value={valorContablePost}
                onChange={(e) => setValorContablePost(e.target.value)}
                min="0.01"
                step="0.01"
                required
              />
            </div>
          </div>

          <div className="generar-tickets-calculated">
            <div className="generar-tickets-calc-item">
              <span className="generar-tickets-calc-label">Tickets a generar</span>
              <span className="generar-tickets-calc-value">{ticketsAGenerar}</span>
            </div>
            <div className="generar-tickets-calc-item">
              <span className="generar-tickets-calc-label">Porcentaje</span>
              <span className="generar-tickets-calc-value">{porcentaje !== '-' ? `${porcentaje}%` : '-'}</span>
            </div>
          </div>

          <div className="generar-tickets-actions">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading || ticketsAGenerar === 0}>
              {loading ? 'Generando...' : `Generar ${ticketsAGenerar} tickets`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerarTicketsModal;
